import { useState, useCallback, useEffect } from "react";
import Cropper, { type Area } from "react-easy-crop";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { blobFromCroppedImage, type PixelCrop } from "@/lib/crop-image";
import { Loader2 } from "lucide-react";

export interface AvatarCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Object URL or remote URL of the image to crop */
  imageSrc: string | null;
  /** Original file MIME type (used for output, falls back to jpeg) */
  mimeType?: string;
  /** Called with cropped file; dialog stays open until parent closes after upload */
  onConfirm: (file: File) => void;
  isApplying?: boolean;
}

export function AvatarCropDialog({
  open,
  onOpenChange,
  imageSrc,
  mimeType = "image/jpeg",
  onConfirm,
  isApplying = false,
}: AvatarCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null);

  useEffect(() => {
    if (open && imageSrc) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc]);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels_: Area) => {
    setCroppedAreaPixels(croppedAreaPixels_);
  }, []);

  async function handleApply() {
    if (!imageSrc || !croppedAreaPixels) return;
    const blob = await blobFromCroppedImage(imageSrc, croppedAreaPixels, mimeType);
    const ext = mimeType === "image/png" ? "png" : "jpg";
    const file = new File([blob], `avatar.${ext}`, {
      type: mimeType === "image/png" ? "image/png" : "image/jpeg",
    });
    onConfirm(file);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md sm:max-w-lg gap-4"
        aria-describedby={undefined}
        onPointerDownOutside={(e) => isApplying && e.preventDefault()}
        onEscapeKeyDown={(e) => isApplying && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Adjust profile picture</DialogTitle>
        </DialogHeader>
        {imageSrc ? (
          <>
            <div className="relative w-full aspect-square max-h-[min(55vh,360px)] mx-auto rounded-lg bg-muted overflow-hidden touch-none">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="space-y-2 px-0.5">
              <Label className="text-sm text-muted-foreground">Zoom</Label>
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.01}
                onValueChange={(v) => setZoom(v[0] ?? 1)}
                disabled={isApplying}
              />
            </div>
          </>
        ) : null}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isApplying}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleApply()}
            disabled={!croppedAreaPixels || isApplying}
            data-testid="button-avatar-crop-apply"
          >
            {isApplying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading…
              </>
            ) : (
              "Apply"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
